import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { RedoEmptyState } from "../../../arbiter-components/empty-state/redo-empty-state";
import { RedoSpinner } from "../../../arbiter-components/loading/redo-spinner";
import { Flex } from "../../../flex";
import {
  CommandPaletteItem,
  useCommandPalette,
} from "../../contexts/command-palette-context";
import * as styles from "./command-palette-search-view.module.css";

export interface CommandPaletteSearchViewProps {
  searchValue: string;
}

interface SearchItemProps {
  item: CommandPaletteItem;
  isSelected: boolean;
}

const SearchItem = memo(function SearchItem({
  item,
  isSelected,
}: SearchItemProps) {
  const itemRef = useRef<HTMLDivElement>(null);
  const { performAction } = useCommandPalette();

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [isSelected]);

  const handleClick = () => {
    performAction?.(item);
  };

  return (
    <div
      className={`${styles.resultItem} ${isSelected ? styles.selected : ""}`}
      onClick={handleClick}
      ref={itemRef}
      style={{ cursor: "pointer" }}
    >
      <div className="flex items-center gap-3 overflow-hidden">
        {item.icon && <div className={styles.resultIcon}>{item.icon}</div>}
        <div className={styles.resultContent}>
          <div className="flex gap-1">
            <div className={styles.resultTitle}>{item.title}</div>
            {item.tags &&
              item.tags.map((tag) => (
                <RedoBadge
                  color={tag.color}
                  key={tag.text}
                  size="sm"
                  text={tag.text}
                />
              ))}
          </div>
          {item.subtitle && (
            <div className={styles.resultSubtitle}>{item.subtitle}</div>
          )}
        </div>
      </div>
    </div>
  );
});

interface SearchGroupProps {
  category: string;
  items: CommandPaletteItem[];
  selectedIndex: number;
  startIndex: number;
  showCategoryHeader?: boolean;
}

const SearchGroup = memo(function SearchGroup({
  category,
  items,
  selectedIndex,
  startIndex,
  showCategoryHeader = true,
}: SearchGroupProps) {
  const categoryHeader = useMemo(() => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }, [category]);

  return (
    <div className={styles.searchGroup}>
      {showCategoryHeader && (
        <div className={styles.categoryHeader}>{categoryHeader}</div>
      )}
      <div className={styles.groupItems}>
        {items.map((item, index) => (
          <SearchItem
            isSelected={startIndex + index === selectedIndex}
            item={item}
            key={item.id}
          />
        ))}
      </div>
    </div>
  );
});

const AskAiRow = memo(function AskAiRow({
  searchValue,
}: {
  searchValue: string;
}) {
  const { state, actions } = useCommandPalette();
  const rowRef = useRef<HTMLDivElement>(null);
  const isSelected = state.selectedIndex === -1;

  const handleClick = useCallback(() => {
    if (state.onAskAI) {
      state.onAskAI(state.searchValue);
      actions.closePalette();
    }
  }, [state, actions]);

  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  }, [isSelected]);

  if (!state.onAskAI || !searchValue.trim()) return null;

  return (
    <div
      className={`${styles.resultItem} ${isSelected ? styles.selected : ""}`}
      onClick={handleClick}
      ref={rowRef}
      style={{ cursor: "pointer" }}
    >
      <Flex align="center" dir="row" gap="md" overflow="hidden">
        <AiStarIcon className={styles.askAiIcon} />
        <span className={styles.resultTitle}>
          Ask us, &ldquo;<span style={{ fontWeight: 500 }}>{searchValue}</span>
          &rdquo;
        </span>
      </Flex>
      <div style={{ flexShrink: 0 }}>
        <RedoBadge color="orange" size="sm" text="⇧ + Enter" />
      </div>
    </div>
  );
});

export const CommandPaletteSearchView = memo(function CommandPaletteSearchView({
  searchValue,
}: CommandPaletteSearchViewProps) {
  const { state } = useCommandPalette();
  const { searchResults, isSearching, selectedIndex } = state;
  const hasActiveFilters = state.filters.some((filter) => filter.enabled);

  const groupedResults = useMemo(() => {
    const groups = new Map<string, CommandPaletteItem[]>();

    for (const item of searchResults) {
      const category = item.category;
      if (!groups.has(category)) {
        groups.set(category, []);
      }
      groups.get(category)!.push(item);
    }

    return Array.from(groups.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [searchResults]);

  const shouldShowCategoryHeaders = useMemo(() => {
    return groupedResults.length > 1;
  }, [groupedResults]);

  if (isSearching) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: "308px" }}>
        <RedoSpinner size="md" />
      </Flex>
    );
  }

  if (!searchValue.trim() && !hasActiveFilters && searchResults.length === 0) {
    return (
      <div className={styles.container}>
        <RedoEmptyState
          Icon={SearchIcon}
          iconColor="gray"
          subtext="Type to search for commands, shortcuts, and more."
          text="Start searching"
        />
      </div>
    );
  }

  if (searchResults.length === 0) {
    return (
      <div className={styles.container}>
        <RedoEmptyState
          Icon={SearchIcon}
          iconColor="gray"
          subtext={`We couldn't find anything matching "${searchValue}". Try different keywords.`}
          text="No results found"
        />
      </div>
    );
  }

  let currentIndex = 0;

  return (
    <div className={styles.container}>
      <div className={styles.resultsList}>
        <AskAiRow searchValue={searchValue} />
        {groupedResults.map(({ category, items }, groupIndex) => {
          const startIndex = currentIndex;
          const group = (
            <div key={category}>
              <SearchGroup
                category={category}
                items={items}
                selectedIndex={selectedIndex}
                showCategoryHeader={shouldShowCategoryHeaders}
                startIndex={startIndex}
              />
              {groupIndex < groupedResults.length - 1 && (
                <div className={styles.groupSeparator} />
              )}
            </div>
          );
          currentIndex += items.length;
          return group;
        })}
      </div>
    </div>
  );
});
